import React, { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import Orb from "./Orb";
import BlurText from "@/router/ui/BlurText";
import AuraNexusLogo from "@/assets/AuraNexus_logo.png";
import PoliceLogo from "@/assets/police_logo.png";
import { DoubaoClient } from "@/services/ai/doubaoClient";
import { getRuntimeAIConfig } from "@/services/ai/config";
import { ConversationContext } from "@/services/ai/types";
import "./IntroPage.css";
import "./Orb.css";

interface Message {
  id: number;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
}

const IntroPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [isChatStarted, setIsChatStarted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [aiClient] = useState(() => new DoubaoClient(getRuntimeAIConfig()));
  const [conversationContext, setConversationContext] =
    useState<ConversationContext>(() =>
      new DoubaoClient(getRuntimeAIConfig()).createContext("intro-session")
    );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 开始对话的处理函数
  const handleStartChat = () => {
    const welcomeMessage: Message = {
      id: Date.now(),
      text: "医生你好，我这两天有点发烧咳嗽，不太舒服。",
      sender: "bot",
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
    setIsChatStarted(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    // Add user message
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // 发送消息到AI
      const response = await aiClient.sendMessage(
        conversationContext,
        userMessage.text
      );
      const assistantContent =
        response.choices[0]?.message?.content || "抱歉，我无法生成回复。";

      // 创建AI回复消息
      const botResponse: Message = {
        id: Date.now() + 1,
        text: assistantContent,
        sender: "bot",
        timestamp: new Date(),
      };

      // 更新对话上下文
      const updatedContext = aiClient.updateContext(
        conversationContext,
        userMessage.text,
        assistantContent
      );
      setConversationContext(updatedContext);

      // 添加AI回复
      setMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      console.error("发送消息失败:", error);

      // 显示错误消息
      const errorMessage: Message = {
        id: Date.now() + 1,
        text:
          error instanceof Error ? error.message : "发送消息失败，请稍后重试。",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setIsChatStarted(false);
    setInputValue("");
    setIsLoading(false);
    // 重置对话上下文
    setConversationContext(
      aiClient.createContext("intro-session-" + Date.now())
    );
  };

  return (
    <div className="intro-page-container">
      {/* Orb Background */}
      <div className="orb-background">
        <Orb
          hoverIntensity={0.4}
          rotateOnHover={true}
          forceHoverState={false}
        />
      </div>

      {/* Content Container */}
      <div className="content-container">
        {!isChatStarted ? (
          <div className="welcome-screen">
            <div className="logo-container">
              <img src={AuraNexusLogo} alt="AuraNexus Logo" className="logo" />
            </div>
            <BlurText
              text="欢迎来到 AuraNexus！"
              delay={50}
              animateBy="words"
              direction="top"
              className="welcome-title"
              stepDuration={0.2}
            />
            <div className="welcome-description">
              <BlurText
                text="你好！很高兴在这里与你相遇。"
                delay={80}
                animateBy="words"
                direction="top"
                className="welcome-greeting"
                stepDuration={0.3}
              />
              <br />
              <BlurText
                text="AuraNexus 是我创建的一个个人项目，源于对医学教育和AI技术的热爱。我希望通过这个小小的平台，提供一个可以随时随地、无压力练习临床问诊技巧的「模拟诊室」。"
                delay={100}
                animateBy="words"
                direction="top"
                className="welcome-intro"
                stepDuration={0.3}
              />
              <br />
              <BlurText
                text="在这里，是一位由AI驱动的基于豆包大模型的「虚拟患者」。TA的所有回答都基于预设的病例，旨在帮助你锻炼临床思维和沟通能力。"
                delay={120}
                animateBy="words"
                direction="top"
                className="welcome-feature"
                stepDuration={0.3}
              />
            </div>

            {/* 开始对话按钮 */}
            <div className="start-chat-container">
              <button onClick={handleStartChat} className="start-chat-btn">
                开始对话
              </button>
            </div>
          </div>
        ) : (
          <div className="chat-area">
            {/* New Chat Button */}
            <div className="chat-header">
              <button onClick={handleNewChat} className="new-chat-btn">
                开始新对话
              </button>
            </div>

            {/* Messages */}
            <div className="messages-container">
              {messages.map((msg) => (
                <div key={msg.id} className={`message-bubble ${msg.sender}`}>
                  <div className="message-content">{msg.text}</div>
                  <div className="message-time">
                    {msg.timestamp.toLocaleTimeString("zh-CN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="message-bubble bot loading">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Input Form - 只在对话开始后显示 */}
      {isChatStarted && (
        <div className="input-container">
          <form className="input-form" onSubmit={handleSubmit}>
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="输入消息..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={isLoading}
                className="message-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="send-button"
              >
                <Send size={20} />
              </button>
            </div>
          </form>

          {/* Important Notice */}
          <div className="important-notice">
            <p className="notice-text">
              <strong>重要提示：</strong>
              所有内容仅为虚拟模拟和学习探索之用，不构成任何真实的医疗指导或建议。
            </p>
          </div>
        </div>
      )}

      {/* 备案信息 */}
      <div className="filing-info">
        <div className="filing-links">
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="filing-link"
          >
            陕ICP备2025076219号-1
          </a>
          <span className="filing-separator">|</span>
          <a
            href="https://beian.mps.gov.cn/#/query/webSearch?code=61011302002156"
            target="_blank"
            rel="noopener noreferrer"
            className="filing-link police-filing"
          >
            <img
              src={PoliceLogo}
              width="16"
              height="16"
              alt="公安备案"
              className="police-logo"
            />
            陕公网安备61011302002156号
          </a>
        </div>
      </div>
    </div>
  );
};

export default IntroPage;
